import React, { useEffect } from 'react';
import qs from 'qs';
import { orderBy, groupBy, uniq, maxBy, omit, isEmpty, sum, sumBy, get, keyBy, omitBy, pick, isUndefined, max, keys, entries, fromPairs } from 'lodash';
import { format as formatDate, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, addDays, addMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import { Button, Badge, Nav, NavItem, NavLink, } from 'reactstrap';
import numeral from 'numeral';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { ResponsiveContainer, BarChart, Bar, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, } from 'recharts';
import ellipsis from 'text-ellipsis';
import ReactPaginate from 'react-paginate';

import { fullPathWithParams } from '../../util';
import { currencies, prefectures, routes, colors, } from '../../shared/config';
import { canUpdateOrder } from '../../shared/abilities';
import firebase, { functions } from '../../firebase';
import { getCollectionData, } from '../../shared/firebase';
import { chart, generateShippingRequestRows, fields, payerFields, paymentMethods, paymentMethod, totalPaymentAmount, generateOrderRows, } from '../../shared/models/order';
import { localeProduct, } from '../../shared/models/product';
import { areaFromPostalCode } from '../../shared/models/setting';
import { fields as userFields, adminFields as adminUserFields } from '../../shared/models/user';
import { computeWithoutTax, fieldDisplayValue } from '../../shared/util';
import AdminPage from '../hocs/AdminPage';
import useFirebaseUser from '../hooks/useFirebaseUser';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useUserTags from '../hooks/useUserTags';
import useCollectionsFetchInTenant from '../hooks/useCollectionsFetchInTenant';
import useCollectionFetchInTenant from '../hooks/useCollectionFetchInTenant';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useQueryParams from '../hooks/useQueryParams';
import useCreateActivity from '../hooks/useCreateActivity';
import TenantLink from '../TenantLink';
import ImportButton from '../ImportButton';
import ExportButton from '../ExportButton';
import QueryDateSelector from '../QueryDateSelector';
import QuerySelector from '../QuerySelector';
import QueryBoolean from '../QueryBoolean';
import QueryText from '../QueryText';
import QueryInput from '../QueryInput';
import OrderExportButton from '../OrderExportButton';
import TrendCharts from '../TrendCharts';
import ComparisonCharts from '../ComparisonCharts';
import useTenant from '../hooks/useTenant';
import { useSettingDocument, tenantAreaSettingRef } from '../../models/setting';
import SavedSearchParameterForm from '../forms/SavedSearchParameterForm';
import PreviewImportButton from '../PreviewImportButton';
import OrderFormButton from '../OrderFormButton';
import { orderImportFields, mapOrderImportRow } from '../../shared/models/orderImport';

const db = firebase.firestore();
const settingsRef = db.collection('settings');
const ordersRef = db.collection('orders');
const usersRef = db.collection('users');
const createUserAndSendEmailVerification = functions.httpsCallable('createUserAndSendEmailVerification');
const productsRef = db.collection('products');
const referrersRef = db.collectionGroup('referrers');
const agentsRef = db.collection('agents');
const agentShopsRef = db.collectionGroup('agentShops');
const productTypesRef = db.collection('productTypes');
const couponsRef = db.collection('coupons');
const qrUrlsRef = db.collection('qrUrls');
const { dimensions, metrics, metricOptions, dateTypes, dateTypeOptions, } = chart;
const prefectureOptions = entries(prefectures).map(([k, v]) => ({ label: v, value: k }));
const tabs = {
  list: { label: '一覧', },
  trend: { label: 'トレンド', },
  comparison: { label: '期間比較', },
};
const itemsPerPage = 100;

export default AdminPage(function AdminOrders(props) {
  const { user, location, history } = props;
  const now = new Date();
  const queryParams = qs.parse(decodeURI(location.search.slice(1)));
  const {
    tab = 'list',
    dateType: [dateType] = ['createdAt'],
    startOn: startOnString = formatDate(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endOn: endOnString = formatDate(endOfMonth(new Date()), 'yyyy-MM-dd'),
    staffs: staffsForFilter,
    prefectures: prefecturesForFilter,
    agents: agentsForFilter,
    productTypes: productTypesForFilter,
    preOrderTypes: preOrderTypesForFilter,
    agentShops: agentShopsForFilter,
    referrers: referrersForFilter,
    routes: routesForFilter,
    areaGroups: areaGroupsForFilter,
    paymentMethods: paymentMethodsForFilter,
    withoutCancel: _withoutCancel = '0',
    withPartsOrder: _withPartsOrder = '0',
    onlyCoupon: _onlyCoupon = '0',
    page: _page = '1',
    keyword: keywordForFilter,
    field: [field] = ['id'],
    text,
    comparedStartOn: comparedStartOnString,
    comparedEndOn: comparedEndOnString,
    displayColumns,
  } = queryParams;
  const withoutCancel = _withoutCancel === '1';
  const withPartsOrder = _withPartsOrder === '1';
  const onlyCoupon = _onlyCoupon === '1';
  const page = Number(_page);
  const startOn = new Date(startOnString);
  const endOn = new Date(endOnString);
  const comparedStartOn = comparedStartOnString ? new Date(comparedStartOnString) : null;
  const comparedEndOn = comparedEndOnString ? new Date(comparedEndOnString) : null;
  const { firebaseUser } = useFirebaseUser();
  const tenant = useTenant();
  const country = tenant.salesCountry ?? 'jp';
  const currency = tenant.currency ?? 'jpy';
  const createActivity = useCreateActivity();

  // 列定義（注文IDは固定表示なので除外）
  const columnDefinitions = {
    cammacsStatus: {
      label: 'CAMMACSステータス',
      renderHeader: () => <th key="cammacsStatus">CAMMACSステータス</th>,
      renderCell: ({ order }) => {
        const { cammacsStatus, isWish = false, forShop, referrerKey, shouldStopBecauseHaveSimilarOrders = false, shouldStopBecauseHaveMultiBodyOrders = false, shouldRespondCancelRequest = false, refundFailed = false } = order;
        return (
          <td key="cammacsStatus">
            <div>{cammacsStatus}</div>
            {isWish && <span className="badge badge-info">おねだり</span>}
            {forShop && isEmpty(referrerKey) && <Badge color="danger" className='ml-1'>要リファラ</Badge>}
            {shouldStopBecauseHaveSimilarOrders && <Badge color="danger" className='ml-1'>同一の注文あり</Badge>}
            {shouldStopBecauseHaveMultiBodyOrders && <Badge color="danger" className='ml-1'>本体複数注文</Badge>}
            {shouldRespondCancelRequest && <Badge color="danger" className='ml-1'>キャンセル希望</Badge>}
            {refundFailed && <Badge color="danger" className='ml-1'>返金失敗</Badge>}
          </td>
        );
      },
      exportValue: _ => _.order.cammacsStatus
    },
    isPreOrder: {
      label: '予約注文',
      renderHeader: () => <th key="isPreOrder">予約注文</th>,
      renderCell: ({ order }) => <td key="isPreOrder">{order.isPreOrder && '予約注文'}</td>,
      exportValue: _ => _.order.isPreOrder ? '予約注文' : ''
    },
    area: {
      label: 'エリア',
      renderHeader: () => <th key="area">エリア</th>,
      renderCell: ({ area }) => (
        <td key="area">
          {area && (
            <span>
              <span>{prefectures[area.prefecture]}</span>
              <span>{area.city}</span>
            </span>
          )}
        </td>
      ),
      exportValue: _ => _.area ? `${prefectures[_.area.prefecture]} ${_.area.city}` : ''
    },
    areaStaff: {
      label: '担当',
      renderHeader: () => <th key="areaStaff">担当</th>,
      renderCell: ({ area }) => <td key="areaStaff">{area && area.user.displayName}</td>,
      exportValue: _ => _.area?.user.displayName
    },
    paymentMethod: {
      label: '決済方法',
      renderHeader: () => <th key="paymentMethod">決済方法</th>,
      renderCell: ({ order }) => {
        const { isWish = false, charge, paymentIntent } = order;
        return (
          <td key="paymentMethod">
            <div>{paymentMethods[paymentMethod(order)].label}</div>
            {isWish && (charge ?? paymentIntent) == null && (<span className="badge badge-secondary">未決済</span>)}
          </td>
        );
      },
      exportValue: _ => paymentMethods[paymentMethod(_.order)].label
    },
    agent: {
      label: '代理店',
      renderHeader: () => <th key="agent">代理店</th>,
      renderCell: ({ agent }) => <td key="agent">{agent && agent.name}</td>,
      exportValue: _ => _.agent?.name
    },
    agentShop: {
      label: '店舗',
      renderHeader: () => <th key="agentShop">店舗</th>,
      renderCell: ({ agentShop }) => <td key="agentShop">{agentShop && agentShop.name}</td>,
      exportValue: _ => _.agentShop?.name
    },
    referrer: {
      label: 'リファラ',
      renderHeader: () => <th key="referrer">リファラ</th>,
      renderCell: ({ referrer }) => <td key="referrer">{referrer && referrer.name}</td>,
      exportValue: _ => _.referrer?.name
    },
    qrUrl: {
      label: '紹介QRコード遷移先',
      renderHeader: () => <th key="qrUrl">紹介QRコード遷移先</th>,
      renderCell: ({ qrUrl }) => <td key="qrUrl">{qrUrl && qrUrl.name}</td>,
      exportValue: _ => _.qrUrl?.name
    },
    referrerUrl: {
      label: '直前URL',
      renderHeader: () => <th key="referrerUrl">直前URL</th>,
      renderCell: ({ order }) => {
        const { referrerUrl } = order;
        return (
          <td key="referrerUrl">
            {!isEmpty(referrerUrl) && (
              <a href={referrerUrl} target='_blank'>
                <span className='text-truncate d-inline-block' style={{ maxWidth: 200 }}>
                  {referrerUrl}
                </span>
                <span className='ml-1 fas fa-external-link-alt' />
              </a>
            )}
          </td>
        );
      },
      exportValue: _ => _.order.referrerUrl
    },
    route: {
      label: 'どちらでお知りになりましたか？',
      renderHeader: () => <th key="route">どちらでお知りになりましたか？</th>,
      renderCell: ({ order }) => <td key="route">{order.route}</td>,
      exportValue: _ => _.order.route
    },
    coupons: {
      label: '優待',
      renderHeader: () => <th key="coupons" style={{ minWidth: 150 }}>優待</th>,
      renderCell: ({ order, allCoupons, user, createActivity }) => {
        const { couponCheckedAt } = order;
        const onCheckCoupon = async () => {
          await order.ref.update({
            couponCheckedAt: couponCheckedAt ? null : new Date(),
            updatedAt: new Date(),
            updatedBy: omitBy(pick(user, ['uid', 'email', 'displayName']), isUndefined)
          });
          await createActivity(couponCheckedAt ? 'uncheckCouponOfOrder' : 'checkCouponOfOrder', user, { orderId: order?.id, uid: order.createdBy.uid });
        };
        
        return (
          <td key="coupons">
            {
              allCoupons.map((coupon) => {
                return (
                  <div key={coupon.id} className="card p-1 mb-1">
                    {coupon.name}
                    {!couponCheckedAt ? <Button color="warning" size="sm" onClick={onCheckCoupon}>未確認</Button> : <Button color='success' size="sm" outline onClick={onCheckCoupon}>確認済</Button>}
                  </div>
                );
              })
            }
          </td>
        );
      },
      exportValue: _ => _.allCoupons.map(coupon => coupon.name).join(', ')
    },
    account: {
      label: 'アカウント',
      renderHeader: () => <th key="account">アカウント</th>,
      renderCell: ({ order }) => (
        <td key="account">
          {order.createdBy && <TenantLink to={`/admin/users/${order.createdBy.uid}`}>{order.createdBy.displayName}</TenantLink>}
        </td>
      ),
      exportValue: _ => _.order.createdBy?.displayName
    },
    products: {
      label: '商品',
      renderHeader: () => <th key="products" style={{ minWidth: 300 }}>商品</th>,
      renderCell: ({ orderItemsWithProduct, tenant }) => (
        <td key="products">
          {orderItemsWithProduct.map((orderItem) => {
            const { product, quantity } = orderItem;
            const { name, } = localeProduct(tenant, product);
            return (
              product != null && (
                <div key={product.id}>
                  <span>{product.code}</span>
                  <span className='ml-2'>{name}</span>
                  <span className='ml-2'>{numeral(quantity).format('0,0')}個</span>
                </div>
              )
            );
          })}
        </td>
      ),
      exportValue: _ => _.orderItemsWithProduct.map(item => `${item.product?.code} ${localeProduct(_.tenant, item.product)?.name} ${numeral(item.quantity).format('0,0')}個`).join(', ')
    },
    amount: {
      label: '合計金額(税込)',
      renderHeader: () => <th key="amount">合計金額(税込)</th>,
      renderCell: ({ order }) => <td key="amount" className='text-right'>{numeral(order.amount).format('0,0')}</td>,
      exportValue: _ => _.order.amount
    },
    discountAmount: {
      label: '割引額(税込)',
      renderHeader: () => <th key="discountAmount">割引額(税込)</th>,
      renderCell: ({ order }) => <td key="discountAmount" className='text-right'>{numeral(order.discountAmount || 0).format('0,0')}</td>,
      exportValue: _ => _.order.discountAmount || 0
    },
    shipmentFee: {
      label: '送料(税込)',
      renderHeader: () => <th key="shipmentFee">送料(税込)</th>,
      renderCell: ({ order }) => <td key="shipmentFee" className='text-right'>{numeral(order.shipmentFee || 0).format('0,0')}</td>,
      exportValue: _ => _.order.shipmentFee || 0
    },
    totalPaymentAmount: {
      label: '支払合計(税込)',
      renderHeader: () => <th key="totalPaymentAmount">支払合計(税込)</th>,
      renderCell: ({ order }) => <td key="totalPaymentAmount" className='text-right'>{numeral(totalPaymentAmount(order)).format('0,0')}</td>,
      exportValue: _ => totalPaymentAmount(_.order)
    },
    receivedOn: {
      label: '入金日',
      renderHeader: () => <th key="receivedOn">入金日</th>,
      renderCell: ({ order }) => <td key="receivedOn">{order.receivedOn && formatDate(order.receivedOn.toDate(), 'yyyy/MM/dd')}</td>,
      exportValue: _ => _.order.receivedOn && formatDate(_.order.receivedOn.toDate(), 'yyyy/MM/dd')
    },
    receivedAmount: {
      label: '入金額',
      renderHeader: () => <th key="receivedAmount">入金額</th>,
      renderCell: ({ order }) => (
        <td key="receivedAmount" className='text-right'>
          {order.receivedAmount != null && numeral(order.receivedAmount).format('0,0')}
        </td>
      ),
      exportValue: _ => _.order.receivedAmount
    },
    stripeFee: {
      label: '決済手数料',
      renderHeader: () => <th key="stripeFee">決済手数料</th>,
      renderCell: ({ order }) => <td key="stripeFee" className='text-right'>{order.stripeFee != null && numeral(order.stripeFee).format('0,0')}</td>,
      exportValue: _ => _.order.stripeFee
    },
    refundedOn: {
      label: 'Stripeへの返金日',
      renderHeader: () => <th key="refundedOn">Stripeへの返金日</th>,
      renderCell: ({ order }) => <td key="refundedOn">{order.refundedOn && formatDate(order.refundedOn.toDate(), 'yyyy/MM/dd')}</td>,
      exportValue: _ => _.order.refundedOn && formatDate(_.order.refundedOn.toDate(), 'yyyy/MM/dd')
    },
    refundAmount: {
      label: 'Stripeへの返金額',
      renderHeader: () => <th key="refundAmount">Stripeへの返金額</th>,
      renderCell: ({ order }) => <td key="refundAmount" className='text-right'>{order.refundAmount != null && numeral(order.refundAmount).format('0,0')}</td>,
      exportValue: _ => _.order.refundAmount
    },
    createdAt: {
      label: '注文日時',
      renderHeader: () => <th key="createdAt">注文日時</th>,
      renderCell: ({ order }) => <td key="createdAt">{formatDate(order.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</td>,
      exportValue: _ => formatDate(_.order.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    cancelledAt: {
      label: 'キャンセル日時',
      renderHeader: () => <th key="cancelledAt">キャンセル日時</th>,
      renderCell: ({ order }) => <td key="cancelledAt">{order.cancelledAt && formatDate(order.cancelledAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</td>,
      exportValue: _ => _.order.cancelledAt && formatDate(_.order.cancelledAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    shippedDate: {
      label: '出荷日',
      renderHeader: () => <th key="shippedDate">出荷日</th>,
      renderCell: ({ order }) => <td key="shippedDate">{order.shippedDate}</td>,
      exportValue: _ => _.order.shippedDate
    },
    wishPaidAt: {
      label: 'おねだり決済日時',
      renderHeader: () => <th key="wishPaidAt">おねだり決済日時</th>,
      renderCell: ({ order }) => <td key="wishPaidAt">{order.wishPaidAt && formatDate(order.wishPaidAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</td>,
      exportValue: _ => _.order.wishPaidAt && formatDate(_.order.wishPaidAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    note: {
      label: '備考',
      renderHeader: () => <th key="note">備考</th>,
      renderCell: ({ order }) => <td key="note">{order.note}</td>,
      exportValue: _ => _.order.note
    },
  };

  // フィールド用の列定義を追加
  const fieldColumnDefinitions = fromPairs(
    entries(omit({ ...fields({ country }), ...payerFields(), }, ['route'])).map(([fieldName, fieldSettings]) => {
      const prefix = fieldName.startsWith('destination') ? '配送先' : fieldName.startsWith('contactor') ? '利用者情報' : fieldName.startsWith('payer') ? '決済者' : '';
      return [
        fieldName,
        {
          label: `${prefix}${fieldSettings.label}`,
          renderHeader: () => <th key={fieldName}>{prefix}{fieldSettings.label}</th>,
          renderCell: ({ order }) => <td key={fieldName}>{fieldDisplayValue(order[fieldName], fieldSettings)}</td>,
          exportValue: _ => fieldDisplayValue(_.order[fieldName], fieldSettings)
        }
      ];
    })
  );

  const allColumnDefinitions = { ...columnDefinitions, ...fieldColumnDefinitions };
  const columnOptions = entries(allColumnDefinitions).map(([value, { label }]) => ({ value, label }));
  const columnsToShow = isEmpty(displayColumns) ? keys(allColumnDefinitions) : displayColumns;
  const { data: areaSetting } = useSettingDocument(tenantAreaSettingRef(tenant?.id));
  const areaGroupOptions = uniq(Object.values(areaSetting?.data || {}).map(_ => _.group)).map(_ => ({ label: _, value: _ }));
  const userTags = useUserTags();
  const userTagsById = keyBy(userTags, 'id');
  const staffs = useCollectionSubscriptionInTenant(usersRef.where('role', 'in', ['admin', 'staff']));
  const staffOptions = staffs.map((_) => ({ label: _.displayName, value: _.id }));
  const products = useCollectionSubscriptionInTenant(productsRef.orderBy('code'));
  const productsById = keyBy(products, 'id');
  const fieldOptions = [
    { label: '注文ID', value: 'id' },
    { label: 'メールアドレス', value: 'email' },
    { label: 'アカウントID', value: 'createdBy.uid' },
    { label: 'アカウント名', value: 'createdBy.displayName' },
    { label: 'アカウント名(かな)', value: 'createdBy.nameKana' },
  ];
  const fieldPath = field === 'id' ? firebase.firestore.FieldPath.documentId() : field;
  const ordersQuery = text
    ? ordersRef.orderBy(fieldPath).startAt(text).endAt(`${text}\uf8ff`)
    : ordersRef
        .where(dateType, '>=', dateTypes[dateType].start(startOn))
        .where(dateType, '<=', dateTypes[dateType].end(endOn))
        .orderBy(dateType, 'desc');
  const { items: orders, isLoading } = useCollectionSubscriptionInTenant(ordersQuery, [dateType, startOnString, endOnString, text, field], { detail: true });
  console.log(3, dateType, dateTypes[dateType].end(endOn), orders);
  const { items: comparedOrders, } = useCollectionSubscriptionInTenant(tab === 'comparison' && comparedStartOn && comparedEndOn && db.collection('orders').where(dateType, '>=', startOfDay(comparedStartOn)).where(dateType, '<=', endOfDay(comparedEndOn)).orderBy(dateType, 'desc'), [tab, comparedStartOn?.toString(), comparedEndOn?.toString()], { detail: true });
  const referrers = useCollectionsFetchInTenant(
    uniq([...orders, ...comparedOrders].flatMap(_ => [_.referrerKey, ...(_.referralLogs || []).map(_ => _.referrerKey)].filter(_ => _))).map((_) => referrersRef.where('key', '==', _)),
    [orders.length, comparedOrders.length]
  );
  const referrersById = keyBy(referrers, 'id');
  const referrerOptions = referrers.map(_ => ({ label: _.name, value: _.id }));
  const qrUrls = useCollectionFetchInTenant(qrUrlsRef);
  const qrUrlsById = keyBy(qrUrls, 'id');
  const agentShops = useCollectionSubscriptionInTenant(agentShopsRef);
  const agentShopsById = keyBy(agentShops, 'id');
  const agentShopOptions = agentShops.map(_ => ({ label: _.name, value: _.id }));
  const agents = useCollectionSubscriptionInTenant(agentsRef);
  const agentOptions = agents.map((_) => ({ label: _.name, value: _.id }));
  const agentsById = keyBy(agents, 'id');
  const productTypes = useCollectionSubscriptionInTenant(productTypesRef.orderBy('index'));
  const coupons = useCollectionSubscriptionInTenant(couponsRef);
  const couponsById = keyBy(coupons, 'id');
  const preOrderTypeOptions = [
    { label: '予約注文', value: 'true' },
    { label: '通常注文', value: 'false' },
  ];
  const paymentMethodOptions = entries(paymentMethods).map(([value, { label }]) => ({ label, value }));
  const magazineGroups = useCollectionSubscriptionInTenant(db.collection('magazineGroups'));
  const [rows, comparedRows] = [orders, comparedOrders].map(orders => orders.filter(_ => !_.isEnvelopeOrder).map((order) => {
    const { orderItems, referrerKey, qrUrlId, destinationPostalCode, contactorPostalCode, wholesaleAgentId, wholesaleAgentShopId, coupon, otherCoupons = [], } = order;
    const allCoupons = [coupon, ...otherCoupons].filter(_ => _);
    const area = areaFromPostalCode(contactorPostalCode || destinationPostalCode, areaSetting);
    const referrer = referrersById[referrerKey];
    const qrUrl = qrUrlsById[qrUrlId];
    const agentShop = agentShopsById[wholesaleAgentShopId || get(referrer, 'ref.parent.parent.id')];
    const agent = agentsById[wholesaleAgentId || get(agentShop, 'ref.parent.parent.id')];
    const referralLogs = (order.referralLogs || []).map((referralLog) => {
      const { referrerKey, referrerKeySavedAt } = referralLog;
      const referrer = referrersById[referrerKey];
      const agentShop = agentShopsById[referrer?.ref.parent.parent.id];
      const agent = agentsById[agentShop?.ref.parent.parent.id];
      return {
        savedAt: new Date(referrerKeySavedAt),
        agent,
        agentShop,
        referrer,
      };
    });
    const orderItemsWithProduct = orderItems.map((orderItem) => {
      const { productId } = orderItem;
      const product = productsById[productId];
      return { ...orderItem, product, order };
    });
    const bodyQuantity = sumBy(orderItemsWithProduct.filter(({ product }) => product?.isBody), 'quantity');
    const bodyAmount = sumBy(orderItemsWithProduct.filter(({ product }) => product?.isBody).map(_ => (_.price ?? localeProduct(tenant, _.product).price) * _.quantity));
    return { order, area, referrer, agentShop, agent, qrUrl, orderItems, orderItemsWithProduct, bodyQuantity, bodyAmount, referralLogs, allCoupons, };
  }));

  // NOTE: filter
  const filterRows = (rows) => {
    let filteredRows = rows;
    let withoutProductTypeFilteredRows = rows;
    let withoutRouteFilteredRows = rows;
    console.log(filteredRows.filter(_ => _.id === '12345-67890-12345-67890'));
    if (!text) {
      if (!isEmpty(staffsForFilter)) {
        filteredRows = filteredRows.filter((_) => staffsForFilter.includes(get(_, 'area.user.id')));
      }
      if (!isEmpty(prefecturesForFilter)) {
        filteredRows = filteredRows.filter((_) => prefecturesForFilter.includes(get(_, 'area.prefecture')));
      }
      if (!isEmpty(agentsForFilter)) {
        filteredRows = filteredRows.filter((_) => agentsForFilter.includes(get(_, 'agent.id')));
      }
      if (!isEmpty(preOrderTypesForFilter)) {
        filteredRows = filteredRows.filter((_) =>
          preOrderTypesForFilter.includes((_.order.isPreOrder || false).toString())
        );
      }
      if (!isEmpty(agentShopsForFilter)) {
        filteredRows = filteredRows.filter((_) => agentShopsForFilter.includes(get(_, 'agentShop.id')));
      }
      if (!isEmpty(referrersForFilter)) {
        filteredRows = filteredRows.filter((_) => referrersForFilter.includes(get(_, 'referrer.id')));
      }
      if (!isEmpty(areaGroupsForFilter)) {
        filteredRows = filteredRows.filter((_) => areaGroupsForFilter.includes(get(_, 'area.group')));
      }
      if (!isEmpty(paymentMethodsForFilter)) {
        filteredRows = filteredRows.filter((_) => paymentMethodsForFilter.includes(paymentMethod(_.order)));
      }
      if (withoutCancel) {
        filteredRows = filteredRows.filter((_) => _.order.cancelledAt == null);
      }
      if (!withPartsOrder) {
        filteredRows = filteredRows.filter((_) => _.orderItemsWithProduct.some((_) => _.product?.isBody));
      }
      if (onlyCoupon) {
        filteredRows = filteredRows.filter((_) => _.order.coupon != null);
      }
      if (keywordForFilter && typeof keywordForFilter === 'string') {
        filteredRows = filteredRows.filter((_) => {
          const {
            id,
            cammacsStatus,
            createdBy,
            referrerUrl,
            route,
            note,
          } = _.order;
          const fieldValues = entries(omit({ ...fields({ country }), ...payerFields(), }, ['route'])).map(([fieldName, fieldSettings]) => {
            return fieldDisplayValue(_.order[fieldName], fieldSettings);
          });
          return id?.includes(keywordForFilter) ||
            cammacsStatus?.includes(keywordForFilter) ||
            `${prefectures[_.area?.prefecture]} ${_.area?.city}`.includes(keywordForFilter) ||
            _.area?.user.displayName?.includes(keywordForFilter) ||
            paymentMethods[paymentMethod(_.order)].label.includes(keywordForFilter) ||
            _.agent?.name.includes(keywordForFilter) ||
            _.agentShop?.name.includes(keywordForFilter) ||
            _.referrer?.name.includes(keywordForFilter) ||
            _.qrUrl?.name.includes(keywordForFilter) ||
            referrerUrl?.includes(keywordForFilter) ||
            route?.includes(keywordForFilter) ||
            note?.includes(keywordForFilter) ||
            _.allCoupons.some(({ name }) => name.includes(keywordForFilter)) ||
            createdBy?.displayName?.includes(keywordForFilter) ||
            _.orderItemsWithProduct.some(({ product }) => product?.code.includes(keywordForFilter) || localeProduct(tenant, product)?.name.includes(keywordForFilter)) ||
            fieldValues.some(v => v.includes(keywordForFilter));
        });
      }
      withoutRouteFilteredRows = filteredRows;
      if (!isEmpty(routesForFilter)) {
        filteredRows = filteredRows.filter((_) => routesForFilter.includes(get(_, 'order.route')));
      }
      withoutProductTypeFilteredRows = filteredRows;
      if (!isEmpty(productTypesForFilter)) {
        [filteredRows, withoutRouteFilteredRows] = [filteredRows, withoutRouteFilteredRows].map(_ => _.filter((_) =>
          _.orderItemsWithProduct.some((_) =>
            get(_, 'product.productTypeIds', []).some((_) => productTypesForFilter.includes(_)) && _.product.isBody
          )
        ));
      }
    }
    return { withoutProductTypeFilteredRows, withoutRouteFilteredRows, filteredRows };
  };
  const [{ withoutProductTypeFilteredRows, withoutRouteFilteredRows, filteredRows }, { filteredRows: filteredComparedRows }] = [rows, comparedRows].map(_ => filterRows(_));
  const productTypeOptions = productTypes.map((productType) => {
    const count = sumBy(withoutProductTypeFilteredRows.flatMap(_ =>
      _.orderItemsWithProduct.filter(_ => (_?.product.productTypeIds || []).includes(productType.id) && _?.product.isBody)
    ), 'quantity');
    return {
      label: (
        <div>
          {productType.name} <span className="text-muted small">({count})</span>
        </div>
      ),
      value: productType.id
    };
  });
  const routeOptions = routes.map((routeName) => {
    const count = sumBy(withoutRouteFilteredRows.flatMap(_ =>
      _.orderItemsWithProduct.filter(_ => _.order.route === routeName && _?.product.isBody)
    ), 'quantity');
    return {
      label: (
        <div>
          {routeName} <span className="text-muted small">({count})</span>
        </div>
      ),
      value: routeName,
    };
  });

  const rowsForExport = async (_rows) => {
    const users = (await usersRef.where('tenantId', '==', tenant?.id).get()).docs.map((_) => ({ id: _.id, ..._.data() }));
    const usersById = keyBy(users, 'id');
    const rowsWithUser = await Promise.all(
      _rows.map(async (row) => {
        const { order } = row;
        const { createdBy } = order;
        const user = !isEmpty(get(createdBy, 'uid')) ? usersById[get(createdBy, 'uid')] : null;
        const userTags = (user?.userTagIds || []).map(_ => userTagsById[_]);
        return {
          ...row,
          user,
          userTags,
        };
      })
    );
    const maxLengthOrderItemsRow = maxBy(rowsWithUser, (_) => _.orderItemsWithProduct.length);
    const orderItemsRowCount = max([maxLengthOrderItemsRow?.orderItemsWithProduct.length, 10])
    const maxLengthChildrenRow = maxBy(rowsWithUser, (_) => get(_, 'user.children', []).length);
    return rowsWithUser.map(({ user, userTags, area, order, referrer, agentShop, agent, qrUrl, orderItemsWithProduct, referralLogs, allCoupons, }) => {
      const {
        id,
        cammacsStatus,
        createdAt,
        createdBy,
        cancelledAt,
        wishPaidAt,
        shippedDate,
        amount,
        discountAmount = 0,
        shipmentFee = 0,
        receivedOn,
        receivedAmount,
        stripeFee,
        refundedOn,
        refundAmount,
        isPreOrder = false,
        isWish = false,
        route,
        referrerUrl,
      } = order;
      return {
        id,
        cammacsStatus,
        areaPrefecture: area && prefectures[area.prefecture],
        areaCity: get(area, 'city'),
        areaStaffName: get(area, 'user.displayName'),
        paymentMethod: paymentMethods[paymentMethod(order)].label,
        agentId: agent && agent.id,
        agentName: agent && agent.name,
        agentShopId: agentShop && agentShop.id,
        agentShopName: agentShop && agentShop.name,
        referrerId: referrer && referrer.id,
        referrerName: referrer && referrer.name,
        referralLogs: referralLogs.map(_ => [
          formatDate(_.savedAt, 'yyyy/MM/dd HH:mm:ss'),
          _.agent?.name || '(代理店不明)',
          _.agentShop?.name || '(店舗不明)',
          _.referrer?.name || '(リファラー不明)',
        ].join(' ')).join('\n'),
        qrUrlId: qrUrl && qrUrl.id,
        qrUrlName: qrUrl && qrUrl.name,
        couponIds: allCoupons.map(_ => _.id).join('\n'),
        couponNames: allCoupons.map(_ => _.name).join('\n'),
        referrerUrl,
        route,
        isPreOrder,
        isWish,
        amount,
        discountAmount,
        shipmentFee,
        totalAmount: amount - discountAmount + shipmentFee,
        receivedOn: receivedOn && formatDate(receivedOn.toDate(), 'yyyy/MM/dd'),
        receivedAmount,
        stripeFee,
        refundedOn: refundedOn && formatDate(refundedOn.toDate(), 'yyyy/MM/dd'),
        refundAmount,
        createdAt: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        daysElapsed: shippedDate && differenceInDays(new Date(), new Date(shippedDate)),
        cancelledAt: cancelledAt && formatDate(cancelledAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        shippedDate,
        userId: createdBy?.uid,
        userTags: userTags.map(_ => _.name).join(' '),
        ...entries({ ...fields({ country }), ...payerFields(), }).reduce((x, [fieldName, fieldSettings]) => {
          return {
            ...x,
            [fieldName]: fieldDisplayValue(order[fieldName], fieldSettings),
          };
        }, {}),
        wishPaidAt: wishPaidAt && formatDate(wishPaidAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        note: order.note,
        ...[...Array(orderItemsRowCount).keys()].reduce((x, _, i) => {
          const orderItem = orderItemsWithProduct[i];
          return {
            ...x,
            [`orderItem_${i}_productId`]: orderItem?.product && orderItem.product.id,
            [`orderItem_${i}_productCode`]: orderItem?.product && orderItem.product.code,
            [`orderItem_${i}_productName`]: orderItem?.product && orderItem.product.name,
            [`orderItem_${i}_quantity`]: orderItem && orderItem.quantity,
          };
        }, {}),
        ...entries({
          ...omit(userFields({ magazineGroups }), ['password', 'currentPassword', 'passwordConfirmation', 'accept']),
          ...adminUserFields({ userTags }),
        }).reduce((x, [fieldName, fieldSettings]) => {
          return {
            ...x,
            [`profile_${fieldName}`]: user && fieldDisplayValue(user[fieldName], fieldSettings),
          };
        }, {}),
        ...get(maxLengthChildrenRow, 'user.children', []).reduce((x, _, i) => {
          const child = get(user, ['children', i]);
          return {
            ...x,
            [`child_${i}_name`]: child && child.name,
            [`child_${i}_birthday`]: get(child, 'birthday.toDate') && formatDate(child.birthday.toDate(), 'yyyy/MM/dd'),
            [`child_${i}_gender`]: child && child.gender,
            [`child_${i}_vehicleExperiences`]: child && (child.vehicleExperiences || []).join(','),
          };
        }, {}),
      };
    });
  };

  const shippingRequestRows = async () => {
    const orders = (
      await getCollectionData(db.collection('orders').where('tenantId', '==', tenant.id).where('cammacsStatus', 'in', ['initial', 'imported']))
    ).filter(_ => _.cancelledAt == null);
    return generateShippingRequestRows(orders, agentsById, agentShopsById, referrersById, productsById);
  };
  const processStripeCsvRow = async (batch, row) => {
    if (isEmpty(row.reporting_category)) return;

    const type = row.reporting_category || row.Type.toLowerCase();
    const fee = row.fee || row.Fees;
    const net = row.net || row.Net;
    const orderId = (row.description || row.Description)?.match(/\d{5}-\d{5}-\d{5}-\d{5}/)?.[0];
    if (!orderId) return;

    const { exists } = await ordersRef.doc(orderId).get();
    if (!exists) return;

    batch.update(
      ordersRef.doc(orderId),
      {
        ...(
          {
            charge: {
              receivedOn: row.automatic_payout_effective_at
                ? new Date(row.automatic_payout_effective_at.slice(0, 10).replace(/-/g, '/'))
                : null,
              stripeFee: parseInt(fee, 10),
              receivedAmount: parseInt(net, 10),
            },
            refund: {
              refundedOn: row.automatic_payout_effective_at
                ? new Date(row.automatic_payout_effective_at.slice(0, 10).replace(/-/g, '/'))
                : null,
              refundAmount: parseInt(net, 10),
            },
          }[type]
        ),
        updatedBy: omitBy(pick(user, ['uid', 'email', 'displayName']), isUndefined),
      }
    );
  };
  const processShippingCsvRow = async (batch, row) => {
    const [, year, month, day] = row['出荷実績日']?.match(/^(\d{4})(\d{2})(\d{2})$/) || [];
    if (!year) return;

    const orderId = row['備考２'] || row['受注番号'];
    if (!orderId) return;

    const { exists } = await ordersRef.doc(orderId).get();
    if (!exists) return;

    batch.update(
      ordersRef.doc(orderId),
      {
        shippedDate: [year, month, day].join('/'),
        trackingNumber: row['送り状番号(代表)'] || null,
        updatedBy: omitBy(pick(user, ['uid', 'email', 'displayName']), isUndefined),
      }
    );
  };

  // アカウント存在チェック・作成
  const findOrCreateUser = async (email, name, phone, postalCode, prefecture, city, address) => {
    try {
      // 既存ユーザーをチェック（tenantId条件なし）
      const existingUsers = await getCollectionData(
        usersRef.where('email', '==', email).limit(1)
      );
      
      if (existingUsers.length > 0) {
        return existingUsers[0];
      }

      // ユーザーが存在しない場合は作成
      const password = uuidv4();
      const userValues = {
        email,
        displayName: name,
        phone,
        postalCode: postalCode?.replace('-', ''),
        prefecture,
        city,
        address,
        tenantIds: [tenant.id],
      };

      const { data: { uid } } = await createUserAndSendEmailVerification({
        userValues,
        password,
        skipsEmailVerification: true,
      });

      return {
        id: uid,
        uid,
        ...userValues,
      };
    } catch (error) {
      console.error('User creation failed:', error);
      throw error;
    }
  };

  const processOrderImportRows = async (rows) => {
    const batch = db.batch();
    
    const processedOrders = await Promise.all(rows.map(async (row) => {
      const {
        id,
        name,
        email,
        phone,
        postalCode,
        prefecture,
        city,
        address,
        destinationName,
        destinationPhone,
        destinationPostalCode,
        destinationPrefecture,
        destinationCity,
        destinationAddress,
        contactorName,
        contactorPhone,
        contactorPostalCode,
        contactorPrefecture,
        contactorCity,
        contactorAddress,
        deliveryDate,
        deliveryTime,
        amount,
        createdAt,
        orderItems,
        productIds,
        route,
        note,
        cammacsStatus,
      } = row;

      if (!id || !name || !email) return null;

      // ユーザーアカウントを取得または作成
      const createdUser = await findOrCreateUser(email, name, phone, postalCode, prefecture, city, address);

      // 注文データを作成
      return {
        id,
        name,
        email,
        phone,
        postalCode,
        prefecture,
        city,
        address,
        destinationName: destinationName || name,
        destinationPhone: destinationPhone || phone,
        destinationPostalCode: destinationPostalCode || postalCode,
        destinationPrefecture: destinationPrefecture || prefecture,
        destinationCity: destinationCity || city,
        destinationAddress: destinationAddress || address,
        contactorName,
        contactorPhone,
        contactorPostalCode,
        contactorPrefecture,
        contactorCity,
        contactorAddress,
        deliveryDate: deliveryDate ? firebase.firestore.Timestamp.fromDate(deliveryDate) : null,
        deliveryTime: deliveryTime ?? null,
        amount: amount || 0,
        orderItems,
        productIds,
        cammacsStatus: cammacsStatus || 'initial',
        tenantId: tenant.id,
        createdAt: firebase.firestore.Timestamp.fromDate(createdAt),
        createdBy: {
          uid: createdUser.uid,
          displayName: createdUser.displayName,
          email: createdUser.email,
        },
        createdByImport: true,
        updatedAt: new Date(),
        updatedBy: omitBy(pick(user, ['uid', 'email', 'displayName']), isUndefined),
        route,
        note,
      };
    }));

    // nullを除外してバッチに追加（Firestoreはundefinedを受け付けないため除去）
    processedOrders.filter(_ => _).forEach(orderData => {
      batch.set(ordersRef.doc(orderData.id), omitBy(orderData, isUndefined));
    });

    await batch.commit();
  };
  const onClickDateButton = ([startOn, endOn]) => {
    const path = fullPathWithParams(
      { startOn: formatDate(startOn, 'yyyy-MM-dd'), endOn: formatDate(endOn, 'yyyy-MM-dd') },
      location
    );
    history.replace(encodeURI(path));
  };
  const pageCount = Math.ceil(filteredRows.length / itemsPerPage);
  const pageOffset = (page - 1) * itemsPerPage;
  const sortedRows = text ? orderBy(filteredRows, _ => _.order.createdAt.toDate(), 'desc') : filteredRows;
  const handlePageClick = (event) => {
    const path = fullPathWithParams({ page: event.selected + 1 }, location);
    history.replace(encodeURI(path));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <div>
      <div className='admin-orders container-fluid py-5 position-relative'>
        <div className='d-flex justify-content-center mb-3'>
          <h4>注文一覧</h4>
        </div>
        <div className='d-flex align-items-end flex-wrap gap-2'>
          <QuerySelector
            isSimple
            width={100}
            paramName='dateType'
            options={dateTypeOptions()}
            label='日付種別'
            defaultValue={['createdAt']}
            isClearable={false}
          />
          <Button onClick={onClickDateButton.bind(null, [addDays(now, -1), addDays(now, -1)])}>昨日</Button>
          <Button onClick={onClickDateButton.bind(null, [now, now])}>今日</Button>
          <Button onClick={onClickDateButton.bind(null, [startOfMonth(now), endOfMonth(now)])}>今月</Button>
          <Button
            onClick={onClickDateButton.bind(null, [startOfMonth(addMonths(now, -1)), endOfMonth(addMonths(now, -1))])}
          >
            先月
          </Button>
          <QueryDateSelector
            paramName='startOn'
            label='開始日'
            history={history}
            location={location}
            defaultValue={startOfMonth(new Date())}
            style={{ width: 150 }}
          />
          <QueryDateSelector
            paramName='endOn'
            label='終了日'
            history={history}
            location={location}
            defaultValue={endOfMonth(new Date())}
            invalid={startOn > endOn}
            style={{ width: 150 }}
          />
          <QuerySelector
            paramName='field'
            options={fieldOptions}
            label='検索フィールド'
            defaultValue={[field]}
            isClearable={false}
          />
          <QueryText paramName='text' label='検索テキスト' />
          <SavedSearchParameterForm {...props} type="order" />
        </div>
        <div className='mt-2 d-flex align-items-end flex-wrap gap-2'>
          <QuerySelector
            paramName='staffs'
            className='ml-0'
            width={200}
            isMulti
            options={staffOptions}
            label='担当で絞込み'
          />
          <QuerySelector
            paramName='prefectures'
            width={200}
            isMulti
            options={prefectureOptions}
            label='エリア都道府県で絞込み'
          />
          <QuerySelector
            paramName='areaGroups'
            width={200}
            isMulti
            options={areaGroupOptions}
            label='エリアグループで絞込み'
          />
          <QuerySelector paramName='agents' width={250} isMulti options={agentOptions} label='代理店で絞込み' />
          <QuerySelector
            paramName='productTypes'
            width={250}
            isMulti
            options={productTypeOptions}
            label='商品種別で絞込み'
          />
          <QuerySelector
            paramName='preOrderTypes'
            width={200}
            isMulti
            options={preOrderTypeOptions}
            label='予約注文かどうかで絞込み'
          />
          <QuerySelector
            paramName='agentShops'
            width={200}
            isMulti
            options={agentShopOptions}
            label='店舗で絞込み'
          />
          <QuerySelector
            paramName='referrers'
            width={200}
            isMulti
            options={referrerOptions}
            label='リファラで絞込み'
          />
          <QuerySelector
            paramName='routes'
            width={200}
            isMulti
            options={routeOptions}
            label='どこで知ったか？で絞込み'
          />
          <QuerySelector
            paramName='paymentMethods'
            width={200}
            isMulti
            options={paymentMethodOptions}
            label='決済方法で絞込み'
          />
          <QueryBoolean paramName='withoutCancel' label='キャンセルを除く' defaultValue={'0'} />
          <QueryBoolean paramName='withPartsOrder' label='パーツ注文も表示する' defaultValue={'0'} />
          <QueryBoolean paramName='onlyCoupon' label='優待有り' defaultValue={'0'} />
          <QueryInput paramName="keyword" label="キーワード絞り込み" />
          <QuerySelector paramName="displayColumns" width={300} isMulti options={columnOptions} label="表示列を選択" maintainOrder />
        </div>
        <Nav tabs className="mt-3">
          {
            entries(tabs).map(([name, { label }]) => {
              return (
                <NavItem key={name}>
                  <NavLink
                    tag={Link}
                    className={classnames({ active: tab === name })}
                    to={`${location.pathname}?${qs.stringify({ ...queryParams, tab: name, })}`}
                  >
                    {label}
                  </NavLink>
                </NavItem>
              );
            })
          }
        </Nav>
        {
          ({
            list: _ => (
              <div className="mt-3">
                <div className='d-flex justify-content-start align-items-end'>
                  <div>
                    <div className="d-flex gap-3 align-items-center mb-1">
                      <Badge className="p-2">　全件　</Badge>
                      <span>注文件数: {filteredRows.length}</span>
                      <span>注文商品数: {sumBy(filteredRows.map((_) => _.orderItems || []).flat(), 'quantity')}</span>
                      <span className="d-flex gap-1">
                        <span>合計金額(税抜):</span>
                        <span>{currencies[currency]?.prefix}{numeral(computeWithoutTax(sumBy(filteredRows, 'order.amount'))).format()}</span>
                      </span>
                      <span className="d-flex gap-1">
                        <span>支払合計(税抜):</span>
                        <span>{currencies[currency]?.prefix}{numeral(computeWithoutTax(sumBy(filteredRows, (_) => totalPaymentAmount(_.order)))).format()}</span>
                      </span>
                    </div>
                    <div className="d-flex gap-3 align-items-center mb-1">
                      <Badge className="p-2">本体のみ</Badge>
                      <span>注文件数: {filteredRows.length}</span>
                      <span>注文商品数: {sumBy(filteredRows.map((_) => _.bodyQuantity))}</span>
                      <span className="d-flex gap-1">
                        <span>合計金額(税抜):</span>
                        <span>{currencies[currency]?.prefix}{numeral(computeWithoutTax(sumBy(filteredRows.map((_) => _.bodyAmount)))).format()}</span>
                      </span>
                      <span className="d-flex gap-1">
                        <span>支払合計(税抜):</span>
                        <span>{currencies[currency]?.prefix}{numeral(computeWithoutTax(sumBy(filteredRows, (_) => totalPaymentAmount({ ..._.order, amount: _.bodyAmount})))).format()}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='d-flex justify-content-end align-items-end gap-1'>
                  <PreviewImportButton 
                    label='受注取込' 
                    fields={orderImportFields()} 
                    mapRow={row => mapOrderImportRow(row, keyBy(products.filter(_ => _.code), 'code'))}
                    onFix={processOrderImportRows}
                    modalProps={{ style: { minWidth: 1200 } }}
                  />
                  <OrderFormButton products={products} processOrderImportRows={processOrderImportRows} />
                  <ImportButton label='入金CSVインポート' processRow={processStripeCsvRow} />
                  {!tenant.useWms && <ImportButton label="出荷" processRow={processShippingCsvRow} />}
                  {!tenant.useWms && <ExportButton label="出荷依頼データ" fileName="出荷依頼.csv" rows={shippingRequestRows} user={user} />}
                  <ExportButton fileName='注文.csv' rows={() => rowsForExport(filteredRows)} hasPersonalInfo user={user} />
                  <OrderExportButton fileName='注文.csv' {...{ rowsForExport, areaSetting, qrUrlsById, agentShopsById, agentsById, productsById, fieldOptions, staffOptions, prefectureOptions, agentOptions, productTypeOptions, preOrderTypeOptions }} hasPersonalInfo user={user} />
                </div>
                <div className='overflow-auto mt-2'>
                  {filteredRows.length > 0 ? (
                    <div>
                      <table className='table'>
                        <thead className='thead-light text-center text-nowrap'>
                          <tr>
                            <th>注文ID</th>
                            {columnsToShow.map(columnKey => allColumnDefinitions[columnKey]?.renderHeader()).filter(Boolean)}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedRows.slice(pageOffset, pageOffset + itemsPerPage).map(
                            (rowData) => {
                              const cellProps = {
                                ...rowData,
                                tenant,
                                user,
                                createActivity
                              };

                              return (
                                <tr key={rowData.order.id}>
                                  <td>
                                    <TenantLink to={`/admin/orders/${rowData.order.id}`}>{rowData.order.id}</TenantLink>
                                  </td>
                                  {columnsToShow.map(columnKey => {
                                    const column = allColumnDefinitions[columnKey];
                                    return column?.renderCell(cellProps);
                                  })}
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                      <div className="d-flex justify-content-center">
                        <ReactPaginate
                          breakLabel="..."
                          previousLabel="<"
                          nextLabel=">"
                          activeClassName="active"
                          disabledClassName="disabled"
                          containerClassName="list-group list-group-horizontal"
                          pageClassName="list-group-item"
                          previousClassName="list-group-item"
                          nextClassName="list-group-item"
                          breakClassName="list-group-item"
                          onPageChange={handlePageClick}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={2}
                          pageCount={pageCount}
                          renderOnZeroPageCount={null}
                        />
                      </div>
                    </div>
                  ) : isLoading ? (
                    <div>
                      <span className="fas fa-spin fa-spinner" />
                    </div>
                  ) : (
                    <div>
                      No Data
                    </div>
                  )}
                </div>
              </div>
            ),
            trend: _ => (
              <TrendCharts {...{ filteredRows, startOn, endOn, productsById }} />
            ),
            comparison: _ => (
              <ComparisonCharts {...{ filteredRows, filteredComparedRows, startOn, endOn, comparedStartOn, comparedEndOn, productsById, history, location }} />
            ),
          })[tab]()
        }
      </div>
      <div className='position-fixed' style={{ bottom: 12, right: 12 }}>
        <Button color="grey" className="text-white bg-grey rounded-circle" style={{ width: 38 }} onClick={() => window.scrollTo(0, document.documentElement.scrollHeight - document.documentElement.clientHeight)}>
          <span className="fas fa-caret-down"></span>
        </Button>
      </div>
    </div>
  );
});
